import { QuoteIneligibleErrorMessages } from '@constants/apiErrors';
import {
  BasketStatus,
  QuoteStatus,
  QuoteUnavailabilityReason,
} from '@generated/gql/graphql';

const getAllBasketReasons = (statuses: QuoteStatus[]) =>
  statuses.reduce((acc, { reasons }) => {
    if (!reasons) {
      return acc;
    }

    return [...acc, ...reasons];
  }, [] as QuoteUnavailabilityReason[]);

const isCbcMissingRequirementsException = (statuses: QuoteStatus[]) => {
  const allReasons = getAllBasketReasons(statuses);

  if (allReasons.length === 0) {
    return false;
  }

  const remainingReasons: QuoteUnavailabilityReason[] = allReasons.filter(
    ({ description }) =>
      description !==
      QuoteIneligibleErrorMessages.atLeastOneAccountHolderRequired
  );

  return remainingReasons.length === 0;
};

// Insurance requires at least one other CORE service to be selected
// CBC is NOT a core service, so this combination is not a valid one
// But because, the quote status reason is not considering the bundle as a whole
// We need to catch this state and provide meaningful error message
const isCbcInsuranceException = (statuses: QuoteStatus[]) => {
  const allReasons = getAllBasketReasons(statuses);

  if (allReasons.length === 0) {
    return false;
  }

  const remainingReasons: QuoteUnavailabilityReason[] = allReasons.filter(
    ({ description }) =>
      description !==
        QuoteIneligibleErrorMessages.insuranceMustBeBundledWithAnotherService &&
      // Cbc missing account reason is acceptable because quote requirements are going to be filled in /your-details
      description !==
        QuoteIneligibleErrorMessages.atLeastOneAccountHolderRequired
  );

  return remainingReasons.length === 0;
};

// Multi SIM requires at least one other CORE service to be selected
// CBC is NOT a core service, so this combination is not a valid one
// But because, the quote status reason is not considering the bundle as a whole
// We need to catch this state and provide meaningful error message
const isCbcMultiSIMException = (statuses: QuoteStatus[]) => {
  const allReasons = getAllBasketReasons(statuses);

  if (allReasons.length === 0) {
    return false;
  }

  const remainingReasons: QuoteUnavailabilityReason[] = allReasons.filter(
    ({ description }) =>
      description !== QuoteIneligibleErrorMessages.simOnly &&
      // Cbc missing account reason is acceptable because quote requirements are going to be filled in /your-details
      description !==
        QuoteIneligibleErrorMessages.atLeastOneAccountHolderRequired
  );

  return remainingReasons.length === 0;
};

const isInsuranceOnlyException = (basketStatus: BasketStatus) => {
  const allReasons = getAllBasketReasons(basketStatus.unavailableStatuses);

  if (allReasons.length === 0) {
    return false;
  }

  const remainingReasons: QuoteUnavailabilityReason[] = allReasons.filter(
    ({ description }) =>
      description !==
      QuoteIneligibleErrorMessages.insuranceMustBeBundledWithAnotherService
  );

  return remainingReasons.length === 0;
};

const isMultiSIMOnlyException = (basketStatus: BasketStatus) => {
  const allReasons = getAllBasketReasons(basketStatus.unavailableStatuses);

  if (allReasons.length === 0) {
    return false;
  }

  const remainingReasons: QuoteUnavailabilityReason[] = allReasons.filter(
    ({ description }) => description !== QuoteIneligibleErrorMessages.simOnly
  );

  return remainingReasons.length === 0;
};

const isEmptyBasketException = (basketStatus: BasketStatus) => {
  const { isValidSelection, unavailableStatuses } = basketStatus;
  return !isValidSelection && unavailableStatuses.length === 0;
};

export {
  isCbcMissingRequirementsException,
  isCbcInsuranceException,
  isCbcMultiSIMException,
  isEmptyBasketException,
  isInsuranceOnlyException,
  isMultiSIMOnlyException,
  getAllBasketReasons,
};
